import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button as MuiButton,
  Grid,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { format, differenceInMinutes, addDays } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import ShiftTimePicker from '@/components/ShiftTimePicker';
import { TimesheetItem } from '../../TimesheetList';
import { getTimesheetReport } from '../../util';
import { axiosRubyClient } from '@/util/axios/axiosClient';

interface EnterHoursModalProps {
  open: boolean;
  onClose: () => void;
  onResolve?: () => void;
  onSaveChanges?: () => void;
  onReject?: () => void;
  onApprove?: () => void;
  workerFullName: string;
  timesheet?: TimesheetItem;
  job: any;
  isClientAdmin: boolean;
  hasMissingCheckout: boolean;
  hasMissingTimesheet: boolean;
  workerInfo?: any;
}

const EnterHoursModal: React.FC<EnterHoursModalProps> = ({
  open,
  onClose,
  onResolve,
  onSaveChanges,
  onReject,
  onApprove,
  workerFullName,
  timesheet,
  job,
  isClientAdmin,
  hasMissingCheckout,
  hasMissingTimesheet,
  workerInfo,
}) => {
  const checkIn = getTimesheetReport(
    timesheet?.approvedCheckinAt || null,
    timesheet?.modifiedCheckinAt || null,
    timesheet?.reportedCheckinAt || null,
    timesheet?.checkinAt || null,
    job.address.timezone,
  );

  const [checkinTime, setCheckinTime] = useState<Dayjs | null>(null);
  const [checkoutTime, setCheckoutTime] = useState<Dayjs | null>(null);
  const [breakMinutes, setBreakMinutes] = useState<number>(0);
  const [selectContainer, setSelectContainer] = useState<HTMLElement | null>(
    null,
  );
  const [checkinError, setCheckinError] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);
  const [breakError, setBreakError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only initialize when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      // Auto-populate check-in time if it exists
      if (checkIn) {
        const checkInDayjs = dayjs(checkIn);
        setCheckinTime(checkInDayjs);
      } else {
        setCheckinTime(null);
      }

      // Auto-populate check-out time if it exists
      const checkout = getTimesheetReport(
        timesheet?.approvedCheckoutAt || null,
        timesheet?.modifiedCheckoutAt || null,
        timesheet?.reportedCheckoutAt || null,
        timesheet?.checkoutAt || null,
        job.address.timezone,
      );
      if (checkout) {
        setCheckoutTime(dayjs(checkout));
      } else {
        setCheckoutTime(null);
      }

      // Auto-populate break minutes if they exist
      setBreakMinutes(
        timesheet?.approvedBreakMinutes ??
          timesheet?.modifiedBreakMinutes ??
          timesheet?.reportedBreakMinutes ??
          0,
      );
      setSelectContainer(document.body);
      setCheckinError(false);
      setCheckoutError(false);
      setBreakError(false);
    } else if (!open && prevOpenRef.current) {
      // Reset state when modal closes
      setCheckinTime(null);
      setCheckoutTime(null);
      setBreakMinutes(0);
      setIsSubmitting(false);
    }
    prevOpenRef.current = open;
  }, [open, checkIn, timesheet, job.address.timezone]);

  const netHours = useMemo(() => {
    const checkInDate = checkinTime || checkIn;
    if (!checkInDate || !checkoutTime) return null;

    // Get the base date (today)
    const today = new Date();
    const baseDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    // Create check-in datetime with today's date
    let checkInDateTime: Date;
    if (checkinTime) {
      const checkInHours = checkinTime.hour();
      const checkInMins = checkinTime.minute();
      checkInDateTime = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        checkInHours,
        checkInMins,
        0,
      );
    } else if (checkInDate instanceof Date) {
      checkInDateTime = checkInDate;
    } else {
      return null;
    }

    // Create check-out datetime with today's date
    const checkoutHours = checkoutTime.hour();
    const checkoutMins = checkoutTime.minute();
    let checkoutDateTime = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      checkoutHours,
      checkoutMins,
      0,
    );

    // If checkout time is less than check-in time, assume it's the next day
    if (checkoutDateTime < checkInDateTime) {
      checkoutDateTime = addDays(checkoutDateTime, 1);
    }

    const minutes =
      differenceInMinutes(checkoutDateTime, checkInDateTime) - breakMinutes;
    const hours = minutes / 60;

    // Ensure net hours is less than 24 hours
    // Truncate to 2 decimal places (not round) for consistency with billing
    const finalHours =
      hours > 0 && hours < 24 ? Math.floor(hours * 100) / 100 : null;
    return finalHours;
  }, [checkinTime, checkIn, checkoutTime, breakMinutes]);

  const handleConfirm = async () => {
    let hasError = false;
    if (!checkinTime) {
      setCheckinError(true);
      hasError = true;
    }
    if (!checkoutTime) {
      setCheckoutError(true);
      hasError = true;
    }
    if (breakMinutes === null || breakMinutes === undefined) {
      setBreakError(true);
      hasError = true;
    }
    console.log(hasError);
    if (hasError) return;

    setIsSubmitting(true);
    try {
      const today = new Date();
      const dateString = format(today, 'yyyy-MM-dd');
      const workerId = workerInfo?.id;

      if (!workerId) {
        console.error('Worker ID not found');
        setIsSubmitting(false);
        return;
      }

      // Format check-in time as HH:mm
      const checkinTimeFormatted = checkinTime
        ? checkinTime.format('HH:mm')
        : checkIn
          ? format(checkIn, 'HH:mm')
          : null;

      // Format check-out time as HH:mm
      const checkoutTimeFormatted = checkoutTime
        ? checkoutTime.format('HH:mm')
        : null;

      // Call the resolve_enter_hours endpoint for both admin types
      await axiosRubyClient.post('/api/admin/timesheets/resolve_enter_hours', {
        jobId: job.id,
        workerId: workerId,
        date: dateString,
        checkinTime: checkinTimeFormatted,
        checkoutTime: checkoutTimeFormatted,
        breakMinutes: breakMinutes,
        timezone: job.address.timezone,
        saveOnly: false,
      });

      window.location.reload();
    } catch (error) {
      console.error('Error resolving timesheet:', error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  };

  const handleSaveChanges = async () => {
    let hasError = false;
    if (!checkinTime) {
      setCheckinError(true);
      hasError = true;
    }
    if (!checkoutTime) {
      setCheckoutError(true);
      hasError = true;
    }
    if (breakMinutes === null || breakMinutes === undefined) {
      setBreakError(true);
      hasError = true;
    }
    if (hasError) return;

    setIsSubmitting(true);
    try {
      const today = new Date();
      const dateString = format(today, 'yyyy-MM-dd');
      const workerId = workerInfo?.id;

      if (!workerId) {
        console.error('Worker ID not found');
        setIsSubmitting(false);
        return;
      }

      // Format check-in time as HH:mm
      const checkinTimeFormatted = checkinTime
        ? checkinTime.format('HH:mm')
        : checkIn
          ? format(checkIn, 'HH:mm')
          : null;

      // Format check-out time as HH:mm
      const checkoutTimeFormatted = checkoutTime
        ? checkoutTime.format('HH:mm')
        : null;

      // Call the resolve_enter_hours endpoint with saveOnly: true
      await axiosRubyClient.post('/api/admin/timesheets/resolve_enter_hours', {
        jobId: job.id,
        workerId: workerId,
        date: dateString,
        checkinTime: checkinTimeFormatted,
        checkoutTime: checkoutTimeFormatted,
        breakMinutes: breakMinutes,
        timezone: job.address.timezone,
        saveOnly: true,
      });

      window.location.reload();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  };

  const breakOptions = [0, 15, 30, 45, 60];

  const description = useMemo(() => {
    if (hasMissingCheckout) {
      return `A missing check-out was detected for ${workerFullName}. Add the missing time and review the entry to resolve the issue.`;
    }
    if (hasMissingTimesheet) {
      return `A missing timesheet was detected for ${workerFullName}. Add the required information to resolve the issue.`;
    }
    return `Enter hours for ${workerFullName}.`;
  }, [workerFullName, hasMissingCheckout, hasMissingTimesheet]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: '500px', width: '100%' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 20px 16px 20px',
        }}
      >
        <Typography
          sx={{
            color: '#1C1C1C',
            textAlign: 'center',
            fontFamily: 'Manrope',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '22.4px',
          }}
        >
          Resolve Timesheet
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '0 20px 20px 20px' }}>
        <Typography
          sx={{
            color: '#1C1C1C',
            fontFamily: 'Manrope',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '22.4px',
            mb: 0.5,
          }}
        >
          Enter Hours for {workerFullName}
        </Typography>
        <Typography
          sx={{
            color: '#5B5B5B',
            fontFamily: 'Manrope',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '22.4px',
            mb: 3,
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            borderRadius: '12px',
            border: '1px solid #EAEAEA',
            background: '#FAFAFA',
            boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
          }}
        >
          <Stack spacing={0}>
            {/* Check-in Field */}
            <Box
              sx={{
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                bgcolor: !checkinTime && !checkIn ? '#FFF4ED' : 'transparent',
                padding: '0 12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333333',
                    }}
                  >
                    Check-in
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  {!checkinTime && !checkIn && (
                    <img
                      src={require('@/assets/icons/exclamation-circle.svg')}
                      alt="Warning"
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: checkinError
                        ? '2px solid #FF6B35'
                        : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '4px',
                      bgcolor: checkinError ? '#FFF4F3' : '#fff',
                      width: '160px',
                      height: '40px',
                    }}
                  >
                    <ShiftTimePicker
                      onChange={(time) => {
                        setCheckinTime(time);
                        setCheckinError(false);
                      }}
                      name="checkin"
                      id="checkin"
                      inputStyle={{
                        border: 'none',
                        width: '100%',
                        padding: '8px 14px',
                        backgroundColor: 'transparent',
                        height: '32px',
                      }}
                      value={checkinTime || null}
                      timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                borderBottom: '1px dashed #B7B7B7',
              }}
            />

            {/* Check-out Field */}
            <Box
              sx={{
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                bgcolor: !checkoutTime ? '#FFF4ED' : 'transparent',
                padding: '0 12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333333',
                    }}
                  >
                    Check-out
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  {!checkoutTime && (
                    <img
                      src={require('@/assets/icons/exclamation-circle.svg')}
                      alt="Warning"
                      style={{
                        width: '20px',
                        height: '20px',
                        marginRight: '8px',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border:
                        checkoutError || (hasMissingCheckout && !checkoutTime)
                          ? '2px solid #FF6B35'
                          : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '4px',
                      bgcolor:
                        checkoutError || (hasMissingCheckout && !checkoutTime)
                          ? '#FFF4F3'
                          : '#fff',
                      width: '160px',
                      height: '40px',
                    }}
                  >
                    <ShiftTimePicker
                      onChange={(time) => {
                        setCheckoutTime(time);
                        setCheckoutError(false);
                      }}
                      name="checkout"
                      id="checkout"
                      inputStyle={{
                        border: 'none',
                        width: '100%',
                        padding: '8px 14px',
                        backgroundColor: 'transparent',
                        height: '32px',
                      }}
                      value={checkoutTime || null}
                      timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                borderBottom: '1px dashed #B7B7B7',
              }}
            />

            {/* Unpaid Break Field */}
            <Box
              sx={{
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333333',
                    }}
                  >
                    Unpaid Break
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <FormControl>
                    <Select
                      value={breakMinutes}
                      onChange={(e) => {
                        setBreakMinutes(Number(e.target.value));
                        setBreakError(false);
                      }}
                      displayEmpty
                      error={breakError}
                      MenuProps={{
                        container: selectContainer,
                        style: {
                          zIndex: 99999,
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            zIndex: 99999,
                          },
                        },
                      }}
                      sx={{
                        borderRadius: '8px',
                        bgcolor: breakError ? '#FFF4F3' : '#fff',
                        border: breakError
                          ? '2px solid #FF6B35'
                          : '1px solid #e0e0e0',
                        width: '160px',
                        height: '40px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiSelect-select': {
                          padding: '8px 14px',
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#333333',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                        },
                      }}
                    >
                      {breakOptions.map((minutes) => (
                        <MenuItem key={minutes} value={minutes}>
                          {minutes} mins
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                borderBottom: '1px dashed #B7B7B7',
              }}
            />

            {/* Net Hours Display */}
            <Box
              sx={{
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#333333',
                  }}
                >
                  Net Hours
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: netHours !== null ? '#333333' : '#9e9e9e',
                  }}
                >
                  {netHours !== null ? `${netHours} hours` : '--'}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Action Buttons */}
        {isClientAdmin ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              mt: 3,
            }}
          >
            <MuiButton
              onClick={onClose}
              variant="outlined"
              sx={{
                display: 'flex',
                width: '160px',
                height: '32px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '4px',
                border: '1px solid #45A735',
                background: '#FFF',
                color: '#2E8722',
                textAlign: 'center',
                fontFamily: 'Manrope',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '16.8px',
                textTransform: 'none',
                '&:hover': {
                  border: '1px solid #45A735',
                  background: '#FFF',
                },
              }}
            >
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={handleConfirm}
              disabled={isSubmitting}
              sx={{
                display: 'flex',
                width: '160px',
                height: '32px',
                padding: '0 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '4px',
                background: isSubmitting ? '#9E9E9E' : '#45A735',
                boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
                color: '#FFF',
                textAlign: 'center',
                fontFamily: 'Manrope',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '16.8px',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: isSubmitting ? '#9E9E9E' : '#45A735',
                  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
                },
                '&:disabled': {
                  background: '#9E9E9E',
                  color: '#FFF',
                },
              }}
            >
              Resolve
            </MuiButton>
          </Box>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 3,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <MuiButton
              onClick={handleSaveChanges}
              disabled={isSubmitting}
              sx={{
                color: isSubmitting ? '#9E9E9E' : '#45A735',
                textTransform: 'none',
                fontWeight: 600,
                padding: 0,
                minWidth: 'auto',
                '&:disabled': {
                  color: '#9E9E9E',
                },
              }}
            >
              Save Changes
            </MuiButton>
            <MuiButton
              onClick={handleConfirm}
              variant="contained"
              disabled={isSubmitting}
              sx={{
                bgcolor: isSubmitting ? '#9E9E9E' : '#45A735',
                '&:hover': { bgcolor: isSubmitting ? '#9E9E9E' : '#45A735' },
                textTransform: 'none',
                fontWeight: 600,
                height: 40,
                '&:disabled': {
                  background: '#9E9E9E',
                  color: '#FFF',
                },
              }}
            >
              Approve
            </MuiButton>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnterHoursModal;
